import { useCallback } from "react";

import { formatDateToFullDate } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

export const useScheduleDonorSubscriptionEnd = ({
  refetch,
  timezone,
}: {
  refetch: () => void;
  timezone?: string | null;
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: execScheduleDonorCommandSubscriptionEnd, isPending: scheduleSubscriptionEndIsLoading } =
    trpc.scheduleDonorCommandSubscriptionEnd.useMutation();

  const scheduleSubscriptionEnd = useCallback(
    async (commandId: string, endDate: string) => {
      const { data, error } = await execScheduleDonorCommandSubscriptionEnd({ id: commandId, endDate });
      if (error || !data) {
        enqueueSnackbar(t("dashboard", "scheduleEndDateDialog.error"), { vibe: "danger" });
        return false;
      }
      refetch();
      enqueueSnackbar(
        t("dashboard", "scheduleEndDateDialog.success", {
          date: formatDateToFullDate(new Date(data.scheduledEndAt), isoLocale, timezone),
        }),
        { vibe: "positive" }
      );
      return true;
    },
    [execScheduleDonorCommandSubscriptionEnd, enqueueSnackbar, t, refetch, isoLocale, timezone]
  );

  return { scheduleSubscriptionEnd, scheduleSubscriptionEndIsLoading };
};
